import { Button, styled } from "@monolith-forensics/monolith-ui";
import { PlusIcon, Trash2Icon } from "lucide-react";
import React, { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { ApiKey } from "./api-keys.types";
import { DeleteApiKeyModal, DisplayApiKeyModal } from "./Modals";
import moment from "moment";
import { useCreateApiKey } from "./api-keys.query";
import LoggingAPI from "@/api/logging";

const ListContainer = styled.div`
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 8px;

  background-color: ${(props) => props.theme.palette.background.default};
`;

const HeaderItemContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  min-height: 50px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const HeaderListItem: React.FC<{ total: number }> = ({ total }) => {
  const { hasPermission, MONOLITH_PERMISSIONS, currentUser } = usePermissions();
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState<ApiKey>();

  const { mutate: createApiKey } = useCreateApiKey({
    onSuccess: (data) => {
      const apiKey = (data as { data: ApiKey }).data;

      LoggingAPI.createLog({
        case_id: undefined,
        user_id: currentUser.user_id,
        message: `Created API key: ${apiKey.uuid}`,
        event: "api-key:create",
        object_type: "api-key",
        object_uuid: apiKey.uuid,
      });

      setNewApiKey(apiKey);
      setShowModal(true);
      setSubmitting(false);
    },
  });

  return (
    <HeaderItemContent>
      {total === 0 ? <div>No API Keys</div> : <div>{total} API Keys</div>}
      <div>
        <Button
          size="xs"
          variant="subtle"
          leftSection={<PlusIcon size={16} />}
          loading={submitting}
          disabled={!hasPermission(MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE)}
          onClick={() => {
            setSubmitting(true);
            createApiKey({});
          }}
        >
          New API Key
        </Button>
      </div>
      <DisplayApiKeyModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        onSubmit={() => {
          setShowModal(false);
        }}
        onCancel={() => {
          setShowModal(false);
        }}
        defaultFormData={newApiKey}
      />
    </HeaderItemContent>
  );
};

const RowItemContent = styled.div`
  user-select: none;

  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 20px;
  min-height: 50px;
  border-top: 1px solid ${(props) => props.theme.palette.divider};
`;

const ApiKeyID = styled.div`
  font-weight: 600;
  // font-size: 16px;
  color: ${(props) => props.theme.palette.text.primary};
`;

const StyledDescription = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const RowItem: React.FC<{ data: ApiKey }> = ({ data }) => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <RowItemContent>
      <div style={{ flexGrow: 1 }}>
        <ApiKeyID>ID: {data.uuid}</ApiKeyID>
        <StyledDescription>
          Created {moment(data?.created_on).format("MMMM D, YYYY")} by{" "}
          {data?.created_by?.full_name}
        </StyledDescription>
      </div>
      <Button
        size="xxs"
        variant="light"
        color="error"
        leftSection={<Trash2Icon size={14} />}
        disabled={!hasPermission(MONOLITH_PERMISSIONS.CUSTOMIZATIONS_MANAGE)}
        onClick={() => setShowDeleteModal(true)}
      >
        Delete
      </Button>
      <DeleteApiKeyModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onSubmit={() => {
          setShowDeleteModal(false);
          enqueueSnackbar("API key deleted successfully", {
            variant: "success",
          });
        }}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        defaultFormData={data}
      />
    </RowItemContent>
  );
};

export const ApiKeyList: React.FC<{
  data: ApiKey[];
}> = ({ data }) => {
  return (
    <ListContainer>
      <HeaderListItem total={data.length} />
      <div>
        {data.map((item) => (
          <RowItem key={item.uuid} data={item} />
        ))}
      </div>
    </ListContainer>
  );
};
